import {
  ResponsiveContainer, AreaChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Flame, Zap, Droplet, Recycle, TreePine, HeartHandshake } from "lucide-react";
import { PALETTE, fmt, fmtMoney, KPI, YoyBadge } from "./_shared";

export function OverviewTab({ overview, emTrend }) {
  const o = overview;
  const trendData = emTrend.map((m) => ({
    month: m.month_label,
    total: m.total_kg,
    baseline: m.baseline_kg,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KPI testId="sust-kpi-co2"   label="Total CO₂e (kg)"  value={fmt(o.emissions.total_co2e_kg)} hint={`${fmt(o.emissions.fuel_litres)} L fuel`} icon={Flame}   tone="danger" />
        <KPI testId="sust-kpi-elec"  label="Electricity (kWh)" value={fmt(o.energy.electricity_kwh)} hint={`${fmt(o.energy.solar_generated_kwh)} kWh solar`} icon={Zap} tone="brand" />
        <KPI testId="sust-kpi-water" label="Water Used (L)"   value={fmt(o.water.litres_used)}      hint={`${o.water.recycled_pct}% recycled`}   icon={Droplet} tone="brand" />
        <KPI testId="sust-kpi-waste" label="Waste Diverted"   value={`${o.waste.diverted_pct}%`}    hint={`${fmt((o.waste.general_kg + o.waste.recyclable_kg + o.waste.hazardous_kg))} kg total`} icon={Recycle} tone="good" />
        <KPI testId="sust-kpi-hab"   label="Hectares Managed" value={fmt(o.habitat.hectares_managed, 1)} hint={`${o.habitat.sites_count} sites`} icon={TreePine} tone="good" />
        <KPI testId="sust-kpi-ppl"   label="Community Hrs"    value={fmt(o.people.community_hours)} hint={fmtMoney(o.people.donations_amount) + " donated"} icon={HeartHandshake} tone="good" />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-medium text-slate-800">CO₂e Emissions vs Prior-FY Baseline</div>
            <div className="text-xs text-slate-500">Current vs same month last year — scope 1 + 2 + 3</div>
          </div>
          <YoyBadge current={o.emissions.total_co2e_kg} baseline={trendData.reduce((s, m) => s + (m.baseline || 0), 0)} />
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={trendData}>
            <defs>
              <linearGradient id="cur" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={PALETTE.s2} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={PALETTE.s2} stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => `${fmt(v)} kg`} contentStyle={{ fontSize: "12px" }} />
            <Legend wrapperStyle={{ fontSize: "11px" }} />
            <Area type="monotone" dataKey="total"    name="Current FY" stroke={PALETTE.s2} fill="url(#cur)" strokeWidth={2.5} />
            <Line type="monotone" dataKey="baseline" name="Prior-FY Baseline" stroke={PALETTE.baseline} strokeWidth={2} strokeDasharray="4 4" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
